import { AnimatePresence, LayoutGroup } from 'framer-motion';
import React, { useEffect, useState } from 'react';

import { KdsTicket, KitchenTicketStatus } from '@/renderer/types/kds';
import { KDS_COLUMNS } from './kdsColumns';
import { KdsTicketCard } from './KdsTicketCard';

interface KdsBoardProps {
  tickets: KdsTicket[];
  transitioningTicketId: string | null;
  onAdvance: (ticketId: string, toStatus: KitchenTicketStatus) => void;
}

export const KdsBoard: React.FC<KdsBoardProps> = ({
  tickets,
  transitioningTicketId,
  onAdvance,
}) => {
  // Ticked centrally (not per-card) so all elapsed-time badges + the delayed
  // highlight stay in sync without running one interval per ticket.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(interval);
  }, []);

  return (
    <LayoutGroup>
      <div className="grid flex-1 grid-cols-1 gap-4 overflow-hidden sm:grid-cols-2 xl:grid-cols-4">
        {KDS_COLUMNS.map((column) => {
          const columnTickets = tickets.filter(
            (t) => t.status === column.status,
          );
          return (
            <div
              key={column.status}
              className="flex min-h-0 flex-col rounded-2xl border border-gray-200 bg-gray-50"
            >
              <div
                className={`flex items-center justify-between rounded-t-2xl border-b px-4 py-3 ${column.headerClasses}`}
              >
                <h3 className="text-lg font-bold">{column.title}</h3>
                <span className="rounded-full bg-white/70 px-2.5 py-1 text-sm font-bold">
                  {columnTickets.length}
                </span>
              </div>

              <div className="max-h-full min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
                {columnTickets.length === 0 ? (
                  <div className="flex h-full min-h-[8rem] items-center justify-center px-2 text-center text-base text-gray-500">
                    No tickets
                  </div>
                ) : (
                  <AnimatePresence mode="popLayout">
                    {columnTickets.map((ticket) => (
                      <KdsTicketCard
                        key={ticket.id}
                        ticket={ticket}
                        column={column}
                        now={now}
                        isTransitioning={transitioningTicketId === ticket.id}
                        onAdvance={onAdvance}
                      />
                    ))}
                  </AnimatePresence>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </LayoutGroup>
  );
};
